import { z } from 'zod';

/**
 * Zod schema for PostHog client configuration
 */
export const PostHogClientConfigSchema = z.object({
  key: z.string().min(1, 'PostHog API key is required'),
  host: z.string().url('PostHog host must be a valid URL'),
  ingestUrl: z
    .string()
    .url('PostHog ingest URL must be a valid URL')
    .optional(),
});

/**
 * Zod schema for PostHog server configuration
 */
export const PostHogServerConfigSchema = z.object({
  key: z.string().min(1, 'PostHog API key is required'),
  host: z.string().url('PostHog host must be a valid URL'),
});

/**
 * Inferred TypeScript types from Zod schemas
 */
export type PostHogClientConfig = z.infer<typeof PostHogClientConfigSchema>;
export type PostHogServerConfig = z.infer<typeof PostHogServerConfigSchema>;

/**
 * Get PostHog client configuration from environment variables with Zod validation
 */
export function getPostHogClientConfig(): PostHogClientConfig | null {
  try {
    const rawConfig = {
      key: process.env.NEXT_PUBLIC_POSTHOG_KEY,
      host: process.env.NEXT_PUBLIC_POSTHOG_HOST,
      ingestUrl: process.env.NEXT_PUBLIC_POSTHOG_INGEST_URL,
    };

    return PostHogClientConfigSchema.parse(rawConfig);
  } catch (error) {
    if (error instanceof z.ZodError) {
      console.warn(
        'PostHog client configuration invalid:',
        error.issues.map((e) => `${e.path.join('.')}: ${e.message}`).join(', '),
      );
    } else {
      console.warn('PostHog client configuration validation failed:', error);
    }
    return null;
  }
}

/** PostHog property name for the deploy-release dimension. */
export const RELEASE_PROPERTY = 'release';

/**
 * Resolve the release marker (deploy commit SHA) for PostHog.
 *
 * This is a new, standalone dimension — distinct from `source`/`surface`/
 * `source_app` — used to correlate metric/error shifts with deploys. The client
 * bundle only sees `NEXT_PUBLIC_*` env vars, so the browser reads
 * `NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA`; server code reads the same value from
 * Vercel's `VERCEL_GIT_COMMIT_SHA`. Both name the same commit, so checking each
 * is not a cross-resource fallback — it's the one fact read from whichever var
 * the runtime exposes.
 *
 * Per the no-fallbacks rule, returns `null` when neither is present (local dev /
 * preview without the var) — never a placeholder like `'unknown'`, so callers
 * can omit the property entirely rather than stamp a fake release.
 */
export function getReleaseVersion(): string | null {
  const sha =
    process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA ||
    process.env.VERCEL_GIT_COMMIT_SHA;

  return sha && sha.length > 0 ? sha : null;
}

/**
 * Get the current environment name for PostHog properties
 */
export function getEnvironment(): string {
  // Server-side: check VERCEL_ENV or NODE_ENV
  if (typeof process !== 'undefined' && process.env) {
    return process.env.VERCEL_ENV || process.env.NODE_ENV || 'unknown';
  }
  // Client-side fallback
  return 'unknown';
}

/**
 * System/auth/env subdomains that are not team subdomains
 */
const IGNORED_SUBDOMAINS = [
  'www',
  'api',
  'admin',
  'auth',
  'team',
  'staging',
  'dev',
  'preview',
];

/**
 * Extract account slug from a hostname's subdomain
 * e.g., sounders.arkero.ai → sounders
 *       sounders.staging.arkero.ai → sounders
 *       auth.arkero.ai → undefined
 */
export function extractAccountSlugFromHostname(
  hostname: string | undefined,
): string | undefined {
  if (!hostname) return undefined;

  // Remove port if present
  const cleanHostname = hostname.split(':')[0] || hostname;

  // Disable for localhost and tunnels
  if (
    cleanHostname === 'localhost' ||
    cleanHostname.includes('localhost') ||
    cleanHostname.includes('ngrok') ||
    cleanHostname.includes('pinggy')
  ) {
    return undefined;
  }

  const parts = cleanHostname.split('.');

  // For main domain (arkero.ai) or single hostname, no subdomain
  if (parts.length <= 2) {
    return undefined;
  }

  const subdomain = parts[0];

  // Check if it's a system/ignored subdomain
  if (!subdomain || IGNORED_SUBDOMAINS.includes(subdomain)) {
    return undefined;
  }

  return subdomain;
}

/**
 * Extract account slug from a path like /home/[account]/...
 * Fallback for localhost where subdomains don't work
 */
export function extractAccountSlugFromPath(
  path: string | undefined,
): string | undefined {
  if (!path) return undefined;
  const match = path.match(/^\/home\/([^/]+)/);
  if (match && match[1] !== '(user)') {
    return match[1];
  }
  return undefined;
}

/**
 * Extract account slug - tries subdomain first, falls back to path
 */
export function extractAccountSlug(
  hostname: string | undefined,
  path: string | undefined,
): string | undefined {
  // Try subdomain first (most reliable in production)
  const fromSubdomain = extractAccountSlugFromHostname(hostname);
  if (fromSubdomain) return fromSubdomain;

  // Fall back to path extraction (for localhost)
  return extractAccountSlugFromPath(path);
}

/**
 * Get PostHog server configuration from environment variables with Zod validation
 */
export function getPostHogServerConfig(): PostHogServerConfig | null {
  try {
    const rawConfig = {
      key: process.env.NEXT_PUBLIC_POSTHOG_KEY,
      host: process.env.NEXT_PUBLIC_POSTHOG_HOST,
    };

    return PostHogServerConfigSchema.parse(rawConfig);
  } catch (error) {
    if (error instanceof z.ZodError) {
      console.warn(
        'PostHog server configuration invalid:',
        error.issues.map((e) => `${e.path.join('.')}: ${e.message}`).join(', '),
      );
    } else {
      console.warn('PostHog server configuration validation failed:', error);
    }
    return null;
  }
}
